/**
 * כניסה — the unified login screen of `docs/login-spec.md` with the personal branding
 * (house icon, "הבית שלנו", the family tagline, the gold accent and the "פרטי לגמרי" footer).
 * Class names are exactly the spec's so both systems stay diff-able.
 */

import { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { he } from '../i18n/he.ts';
import { Icon } from '../components/Icon.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { useRouter } from '../hooks/useRouter.tsx';
import { nextAfterLogin } from '../router.ts';
import { errorMessage } from '../api-select.ts';

const GLYPH = (
  <svg viewBox="0 0 320 80" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M4 62C30 58 44 40 70 44S110 66 136 50 176 18 204 30 250 48 276 34 306 12 316 10" />
    <path d="M4 76h312" strokeDasharray="2 6" opacity=".6" />
  </svg>
);

export function LoginScreen() {
  const { api, setUser, toast } = useApp();
  const { navigate, route } = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [pending, setPending] = useState(false);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = emailRef.current?.value.trim() ?? '';
    const password = passwordRef.current?.value ?? '';
    setEmailError(!email);
    setPasswordError(!password);
    if (!email) {
      emailRef.current?.focus();
      return;
    }
    if (!password) {
      passwordRef.current?.focus();
      return;
    }
    const remember = (document.getElementById('login-remember') as HTMLInputElement | null)?.checked ?? true;
    setPending(true);
    api.login({ email, password, remember }).then(
      (response) => {
        setPending(false);
        setUser(response.user);
        navigate(nextAfterLogin(route.query.next), { replace: true });
        toast(response.user.role === 'member' ? he.login.welcomeF(response.user.displayName) : he.login.welcome(response.user.displayName));
      },
      (error: unknown) => {
        setPending(false);
        setPasswordError(true);
        toast(errorMessage(error), { error: true });
        passwordRef.current?.focus();
      },
    );
  };

  return (
    <div className="login-shell">
      <section className="login-brand" aria-label={he.login.name}>
        <div className="login-logo" aria-hidden="true">
          <Icon name="home" />
        </div>
        <h1 className="login-name">{he.login.name}</h1>
        <p className="login-tagline">{he.login.tagline}</p>
        <div className="login-glyph" aria-hidden="true">
          {GLYPH}
        </div>
      </section>

      <section className="login-card" aria-labelledby="login-title">
        <h2 id="login-title">{he.login.title}</h2>
        <form id="login-form" noValidate onSubmit={submit}>
          <div className={`field${emailError ? ' invalid' : ''}`}>
            <label htmlFor="login-email">{he.login.email}</label>
            <input
              ref={emailRef}
              className="input"
              id="login-email"
              type="email"
              dir="ltr"
              autoComplete="username"
              inputMode="email"
              spellCheck={false}
              aria-describedby="login-email-err"
              aria-invalid={emailError || undefined}
              autoFocus
              onChange={() => setEmailError(false)}
            />
            <div className="field-error" id="login-email-err" role="alert" hidden={!emailError}>
              <Icon name="alertCircle" className="xs" />
              <span>{he.login.emailRequired}</span>
            </div>
          </div>

          <div className={`field${passwordError ? ' invalid' : ''}`}>
            <label htmlFor="login-pw">{he.login.password}</label>
            <div className="pw">
              <input
                ref={passwordRef}
                className="input"
                id="login-pw"
                type={showPassword ? 'text' : 'password'}
                dir="ltr"
                autoComplete="current-password"
                aria-describedby="login-pw-err"
                aria-invalid={passwordError || undefined}
                onChange={() => setPasswordError(false)}
              />
              <button
                type="button"
                className="btn-icon"
                aria-pressed={showPassword}
                aria-label={showPassword ? he.login.hidePassword : he.login.showPassword}
                aria-controls="login-pw"
                onClick={() => setShowPassword((value) => !value)}
              >
                <Icon name={showPassword ? 'eyeOff' : 'eye'} />
              </button>
            </div>
            <div className="field-error" id="login-pw-err" role="alert" hidden={!passwordError}>
              <Icon name="alertCircle" className="xs" />
              <span>{he.login.passwordRequired}</span>
            </div>
          </div>

          <label className="check">
            <input type="checkbox" id="login-remember" defaultChecked />
            {he.login.remember}
          </label>

          {api.isMock ? (
            <div className="chip accent" style={{ alignSelf: 'center' }} data-demo-only>
              <Icon name="info" className="xs" />
              {he.login.demoHint}
            </div>
          ) : null}

          <button type="submit" className="btn primary lg block" disabled={pending}>
            {he.login.submit}
          </button>

          <div className="row between">
            <button type="button" className="link" onClick={() => toast(he.login.forgotToast)}>
              {he.login.forgot}
            </button>
            <span className="meta">{he.login.passkey}</span>
          </div>
        </form>
      </section>

      <div className="login-foot">{he.login.foot}</div>
    </div>
  );
}
